import { houseWashingHeroOpt as houseWashingHero } from "../assets/opt";
import roofWashingHero from "../assets/service-heroes/service-roof-washing-custom.webp.asset.json";
import { paverCliftonParkHeroOpt as paverCleaningHeroCliftonPark } from "../assets/opt";
import concreteCleaningHero from "../assets/service-heroes/service-concrete-cleaning-sealing-v2.webp.asset.json";

export type GuideSection = {
  heading: string;
  paragraphs: string[];
  bullets?: string[];
};

export type GuideFaq = { q: string; a: string };

export type GuideRelatedLink = { label: string; to: string };

export type Guide = {
  slug: string;
  title: string;
  description: string;
  published: string;
  /** Shorter title used for the <title> tag / SERP snippet (<60 chars). */
  metaTitle: string;
  heroImage: string;
  intro: string;
  sections: GuideSection[];
  faqs: GuideFaq[];
  relatedLinks: GuideRelatedLink[];
};

import { GUIDES_HARDSCAPE } from "./guides-hardscape";
import { GUIDES_SEASONAL } from "./guides-seasonal";
import { GUIDES_COST } from "./guides-cost";

const GUIDES_CORE: Guide[] = [
  {
    slug: "black-streaks-on-roof-albany",
    metaTitle: "Black Streaks on My Roof? | Albany NY Guide",
    title: "Why Are There Black Streaks on My Roof? (Albany NY Roof Cleaning Guide)",
    description:
      "Those black streaks on an Albany-area roof are algae, not dirt. What causes them and how soft washing removes them safely.",
    published: "2026-08-18",
    heroImage: roofWashingHero.url,
    intro:
      "If you've noticed dark streaks running down your shingles — usually on the north-facing slope, or the side of the roof that gets the least sun — you're not imagining it, and it isn't just dirt. It's an organism, and it's been eating your roof for months or years. Here's what's actually going on, why it's common in Albany and the greater Capital Region, and the right way to deal with it without voiding your shingle warranty.",
    sections: [
      {
        heading: "What Are Those Black Streaks, Really?",
        paragraphs: [
          "The dark stains that show up on asphalt shingle roofs are almost always caused by a blue-green algae called Gloeocapsa magma. It travels by airborne spores and lands on roofs everywhere, but it thrives in humid climates with plenty of shade and moisture — which describes the Capital Region for a good chunk of the year, especially in tree-covered neighborhoods around Albany, Delmar, and Niskayuna.",
          "The algae feeds on the limestone filler used in most asphalt shingles. As colonies grow, they develop a dark, UV-protective coating (similar to a protective 'shell'), which is what you're actually seeing as black or dark green-gray streaking. It typically starts small, near vents, valleys, or the north slope where sunlight and drying airflow are limited, then spreads across the whole roof over a few seasons.",
          "It's often confused with soot, tree sap, or mold, but the streaking pattern — long vertical drips that follow water runoff — is the giveaway. It's a living colony, not a stain you can just wipe off.",
        ],
      },
      {
        heading: "Why Black Streaks Are So Common in the Capital Region",
        paragraphs: [
          "Albany, Schenectady, and Troy all sit close to the Hudson and Mohawk rivers, and the region gets a healthy mix of humid summers and heavy tree cover in older, established neighborhoods. That combination — moisture plus shade — is exactly what algae needs to establish and spread. Homes with mature trees nearby, or roofs that don't get much direct afternoon sun, tend to develop streaking faster and more severely than homes in open, sunny lots.",
          "We also see it accelerate on roofs that already have some age on them, since the limestone filler becomes more exposed as the shingle granules wear down slightly over the years.",
        ],
      },
      {
        heading: "Is It Just Cosmetic, or Does It Damage the Roof?",
        paragraphs: [
          "Both. Cosmetically, streaking drags down curb appeal and can make a well-maintained home look neglected — which matters if you're selling or just proud of your property. But it's more than appearance: as algae (and often moss, in shadier spots) builds up, it holds moisture against the shingle surface. Over time this can accelerate granule loss, which shortens the effective life of the roof and reduces its ability to reflect heat and shed water properly.",
          "Left untreated for years, especially alongside moss growth in valleys or under overhanging branches, it can contribute to shingle lifting and, eventually, leaks.",
        ],
      },
      {
        heading: "Why You Should Never Pressure Wash a Roof",
        paragraphs: [
          "This is the part that trips up a lot of homeowners and even some inexperienced cleaning companies. Asphalt shingles are not designed to handle direct high-pressure water. Blasting a roof with a pressure washer strips away the protective granules, which are what shield the shingle from UV rays and the elements. Once those granules are gone, they don't grow back, and the shingle degrades much faster.",
          "Most shingle manufacturers explicitly state that pressure washing voids the warranty. It's also just dangerous — steep, wet asphalt shingles and pressure washing equipment are not a safe combination for a DIYer on a ladder.",
        ],
        bullets: [
          "Pressure washing strips protective granules from asphalt shingles",
          "It can void manufacturer warranties (ARMA specifically warns against it)",
          "It doesn't kill the algae at the root — it just knocks off the visible colony, which regrows quickly",
        ],
      },
      {
        heading: "The Right Way: Soft Washing",
        paragraphs: [
          "Soft washing uses low-pressure application of an algaecide/fungicide solution designed specifically for roofing materials. Instead of physically blasting the algae off, the solution kills the organism at the root, including spores that haven't visibly bloomed yet. The dead algae then rinses away naturally with rain over the following days and weeks, revealing clean shingles underneath without any mechanical stress on the roofing material.",
          "This is the method recommended by roofing manufacturers and the one we use on every roof cleaning job in the Capital Region. It's safe for shingles, safe for the gutters and landscaping below (we take care to protect plants during application), and it lasts far longer than a pressure wash because it addresses the actual cause instead of just the symptom.",
        ],
      },
    ],
    faqs: [
      {
        q: "Will the black streaks come right back after cleaning?",
        a: "Soft washing kills the algae colony at the root, so regrowth is much slower than after a pressure wash. Most Capital Region homeowners go 2-3+ years before streaking becomes noticeable again, depending on tree cover and roof orientation.",
      },
      {
        q: "Can I clean the streaks off myself?",
        a: "We don't recommend it. Roofs are dangerous to work on, and store-bought sprays often use bleach concentrations that can be harsh on landscaping or gutters if not applied and rinsed correctly. A professional soft wash is safer and more effective long-term.",
      },
      {
        q: "Does soft washing hurt my shingles or shorten their life?",
        a: "No — it's the opposite. Soft washing is the manufacturer-approved cleaning method precisely because it doesn't strip granules the way high-pressure water does. Removing the algae also removes the moisture-holding buildup that can shorten a roof's life.",
      },
      {
        q: "How much does roof cleaning cost in the Albany area?",
        a: "Pricing depends on roof size, pitch, and how established the algae growth is. Request a free quote and we'll give you an exact number for your home.",
      },
      {
        q: "What time of year is best for roof cleaning?",
        a: "Spring through fall works well, whenever temperatures are safely above freezing. Many homeowners clean in spring to remove winter buildup, or in fall to head into winter with a clean roof.",
      },
    ],
    relatedLinks: [
      { label: "Roof Washing Service", to: "/roof-washing" },
      { label: "House Washing Service", to: "/house-washing" },
      { label: "Pressure Washing in Albany, NY", to: "/pressure-washing-albany-ny" },
    ],
  },
  {
    slug: "soft-wash-vs-pressure-wash",
    metaTitle: "Soft Wash vs. Pressure Wash | Capital Region NY",
    title: "Soft Wash vs. Pressure Wash: Which Does Your Capital Region Home Need?",
    description:
      "Soft washing and pressure washing aren't interchangeable. Learn the real differences, which surfaces need which method, and how to avoid costly damage.",
    published: "2026-08-18",
    heroImage: houseWashingHero.url,
    intro:
      "\"Pressure washing\" gets used as a catch-all term for exterior cleaning, but soft washing and pressure washing are genuinely different processes, using different pressure levels, different chemistry, and — most importantly — appropriate for different surfaces. Using the wrong one can mean stripped paint, damaged siding, or a wash that doesn't actually last. Here's how to tell them apart and which your home actually needs.",
    sections: [
      {
        heading: "The Core Difference: Pressure vs. Chemistry",
        paragraphs: [
          "Pressure washing relies on mechanical force — high-pressure water, often 2,500-4,000 PSI, to physically blast dirt, grime, and buildup off a hard surface. It's fast and effective on surfaces built to take that kind of impact.",
          "Soft washing uses low pressure (often under 500 PSI, similar to a garden hose) combined with a specialized cleaning solution — typically a diluted blend that includes surfactants and an algaecide/fungicide. The solution does the actual work of breaking down and killing organic growth like algae, mold, and mildew at the root. The low-pressure rinse simply washes away what's already been killed and loosened.",
        ],
      },
      {
        heading: "When Pressure Washing Is the Right Tool",
        paragraphs: [
          "Hard, non-porous surfaces built to withstand force are good candidates for pressure washing: concrete driveways and sidewalks, paver patios, brick, and some stone. These surfaces don't have paint or a delicate finish to strip, and dirt and mildew here are usually more ground-in, requiring the mechanical push that higher PSI provides.",
        ],
        bullets: [
          "Concrete driveways, walkways, and patios",
          "Paver patios (with appropriate pressure to protect joint sand)",
          "Brick and some masonry surfaces",
        ],
      },
      {
        heading: "When Soft Washing Is Required, Not Optional",
        paragraphs: [
          "Vinyl and wood siding, stucco, roof shingles, and painted surfaces are all vulnerable to high-pressure water. Pressure washing siding can force water behind panels and into your walls, crack or dent vinyl, strip paint, and damage caulking around windows and doors. On roofs, as covered in our roof cleaning guide, high pressure strips protective granules and can void manufacturer warranties outright.",
          "Soft washing is the only appropriate method for these surfaces. It's gentle enough not to cause physical damage, but the algaecide solution is actually more effective than pressure alone at killing the mold, algae, and mildew that cause most of the staining homeowners are trying to remove in the first place.",
        ],
        bullets: [
          "Vinyl, wood, and fiber-cement siding",
          "Asphalt shingle roofs",
          "Stucco and EIFS systems",
          "Painted wood trim, fences, and decks",
        ],
      },
      {
        heading: "Why Soft Washing Lasts Longer",
        paragraphs: [
          "A pressure-only wash on siding might look clean the day it's done, but because it doesn't kill the algae's root structure or spores, regrowth often shows up within a few months. Soft washing addresses the biological cause, not just the visible symptom, which is why homes we soft wash typically stay visibly clean for 1-2+ years depending on shade and moisture exposure, versus a matter of months with pressure alone.",
        ],
      },
      {
        heading: "What a Professional Crew Actually Does",
        paragraphs: [
          "In practice, most full-service exterior cleaning jobs use both methods on the same visit: soft washing for the siding, roof, and any painted surfaces, and higher-pressure washing for the driveway, walkway, and patio. That's exactly how we approach every job — matching the method to the surface, not using one setting for the whole house.",
        ],
      },
    ],
    faqs: [
      {
        q: "Can pressure washing damage my house?",
        a: "Yes. High-pressure water on siding, trim, or roofing can force water behind panels, crack vinyl, strip paint, and damage caulking. That's why we soft wash those surfaces instead of using high pressure.",
      },
      {
        q: "Is soft washing safe for plants and landscaping?",
        a: "Yes, when applied correctly. We pre-wet and protect landscaping and rinse thoroughly, and the solutions we use are formulated to be safe for plants when applied by a trained crew.",
      },
      {
        q: "Which is cheaper, soft washing or pressure washing?",
        a: "Pricing depends more on the size of the job and level of buildup than the method itself. Ask for a free quote and we'll break down exactly what your home needs.",
      },
      {
        q: "Do I need both services for my house?",
        a: "Most homes do — soft wash for siding and roof, pressure wash for concrete and pavers. We typically combine both in a single visit.",
      },
      {
        q: "How often should I get my home soft washed?",
        a: "Most Capital Region homes benefit from a soft wash every 1-2 years, depending on tree cover, shade, and how much moisture the property holds onto.",
      },
    ],
    relatedLinks: [
      { label: "House Washing Service", to: "/house-washing" },
      { label: "Roof Washing Service", to: "/roof-washing" },
      { label: "Concrete & Driveway Cleaning", to: "/concrete-cleaning-sealing" },
    ],
  },
  {
    slug: "siding-cleaning-clifton-park",
    metaTitle: "Siding Cleaning in Clifton Park, NY | Guide",
    title: "Siding Cleaning in Clifton Park: Pollen, Clay Dust, and Tree Sap",
    description:
      "Clifton Park siding takes a beating from pollen, roadside clay dust, and tree sap. Here's why, and how professional soft-wash siding cleaning fixes it.",
    published: "2026-08-18",
    heroImage: houseWashingHero.url,
    intro:
      "Clifton Park homeowners deal with a specific combination of siding grime that's a little different from the rest of the Capital Region: heavy spring pollen from the area's mature tree canopy, reddish clay dust kicked up along roadsides and newer developments, and sticky tree sap dripping from maples and pines onto siding and driveways below. Here's what's actually happening on your siding and the right way to get it clean again.",
    sections: [
      {
        heading: "Why Clifton Park Siding Gets So Dirty, So Fast",
        paragraphs: [
          "Clifton Park's mix of established, tree-lined neighborhoods and newer developments built near cleared land creates a perfect storm for siding grime. In spring, thick yellow-green pollen coats every exterior surface, and it doesn't just wash away in the rain — it tends to bind with humidity and dust into a dull, chalky film. In areas near construction, cleared lots, or unpaved shoulders, reddish clay dust kicks up with traffic and wind, settling into the texture of vinyl and fiber-cement siding, especially on north- and west-facing walls that don't get direct sun to help dry it off.",
          "On top of that, mature maples, oaks, and pines throughout Clifton Park drop sap, especially in late spring and early summer. Sap is sticky and acidic, and it attracts more dirt and pollen to whatever it lands on, compounding the buildup on siding directly under tree canopy.",
        ],
      },
      {
        heading: "Why a Garden Hose Doesn't Cut It",
        paragraphs: [
          "Pollen film, clay dust, and tree sap all have one thing in common: they don't rinse away with plain water. Pollen and clay dust bind to the surface texture of siding, and sap is essentially a natural adhesive — a hose spray will bead off the surface without touching what's actually stuck to it. DIY scrubbing can help, but it's labor-intensive, hard to do safely on a two-story home, and risks scratching vinyl if the wrong tools are used.",
        ],
      },
      {
        heading: "The Soft Wash Solution",
        paragraphs: [
          "Soft washing uses a cleaning solution that breaks down organic buildup — pollen, sap residue, algae — at a chemical level, rather than relying on force to physically scrub it off. The solution is applied at low pressure, given time to work, and then rinsed. This lifts pollen film and sap residue without scrubbing, and it's gentle enough not to damage vinyl, trim, or the caulking around windows and doors.",
          "For clay dust specifically, the same low-pressure rinse combined with the cleaning solution loosens embedded particles from the siding's texture far more effectively and evenly than a hose or high-pressure spray, which can just push dust deeper into seams and overlaps.",
        ],
        bullets: [
          "Breaks down sap residue instead of forcing you to scrape or scrub it",
          "Lifts pollen film evenly across the whole house, including shaded, hard-to-reach walls",
          "Rinses embedded clay dust out of siding texture and seams",
          "Safe for vinyl, fiber-cement, and painted wood siding",
        ],
      },
      {
        heading: "When to Clean Your Clifton Park Home",
        paragraphs: [
          "Late spring, after peak pollen season has passed but before summer sap season fully sets in, is a popular window for many homeowners. That said, early fall is also a great time — it clears off a summer's worth of sap and dust buildup before winter, so your siding starts the new year clean rather than trapping grime under snow and ice for months.",
        ],
      },
    ],
    faqs: [
      {
        q: "Will soft washing remove tree sap from my siding?",
        a: "Yes — our soft wash solution breaks down sap residue so it rinses away, rather than requiring scrubbing that can scratch vinyl siding.",
      },
      {
        q: "How often does Clifton Park siding need to be cleaned?",
        a: "Most homes with heavy tree cover benefit from an annual cleaning, ideally in late spring or early fall, to stay ahead of pollen and sap buildup.",
      },
      {
        q: "Is soft washing safe for newer vinyl siding?",
        a: "Yes. Soft washing uses low pressure specifically because it's gentle on vinyl, unlike high-pressure washing which can crack or dent panels.",
      },
      {
        q: "Can you clean just one side of the house if that's where the buildup is worst?",
        a: "We can, though most homeowners choose a full house wash since pollen and dust typically build up unevenly across all sides over a season.",
      },
      {
        q: "Does cleaning help prevent future staining?",
        a: "Removing existing organic buildup slows down how quickly algae and mildew can take hold again, since they often grow on top of existing grime and pollen film.",
      },
    ],
    relatedLinks: [
      { label: "House Washing Service", to: "/house-washing" },
      { label: "Siding Cleaning Service", to: "/siding-cleaning" },
      { label: "Pressure Washing in Clifton Park, NY", to: "/pressure-washing-clifton-park-ny" },
    ],
  },
  {
    slug: "how-often-pressure-wash-upstate-ny",
    metaTitle: "How Often to Pressure Wash in Upstate NY",
    title: "How Often Should You Pressure Wash in Upstate NY's Freeze-Thaw Climate?",
    description:
      "Freeze-thaw winters change how often Capital Region homes need washing. A month-by-month schedule for Upstate NY.",
    published: "2026-08-18",
    heroImage: concreteCleaningHero.url,
    intro:
      "Upstate New York's climate doesn't just make winters cold — the repeated freeze-thaw cycles through late fall, winter, and early spring actively work grime, salt, and moisture into your home's exterior surfaces in ways that milder climates don't experience. That changes how often different parts of your property actually need cleaning. Here's a realistic, surface-by-surface schedule for the Capital Region.",
    sections: [
      {
        heading: "Why Freeze-Thaw Cycles Matter for Cleaning Frequency",
        paragraphs: [
          "When temperatures swing above and below freezing repeatedly — which the Capital Region experiences dozens of times each winter — any moisture trapped in concrete, siding seams, or roof shingles expands and contracts. This doesn't just risk structural cracking over years; it also drives dirt, algae spores, and salt residue deeper into surface pores and texture, making buildup harder to remove the longer it sits. It's also why algae and mildew that take hold in fall can look dramatically worse by the following spring — they've had a whole winter of trapped moisture to spread.",
        ],
      },
      {
        heading: "House Washing: Every 12-24 Months",
        paragraphs: [
          "Most Capital Region homes do well with a soft wash every one to two years. Homes with heavy tree cover, north-facing shade, or proximity to the river valleys (which hold more humidity) tend to need the shorter end of that range. Homes on open, sunny lots with good airflow can often stretch closer to two years between washes.",
          "A practical rule: wash in fall to clear the season's pollen and algae growth before it sits trapped under snow all winter, or in spring to remove the grime and salt spray that accumulated over the cold months.",
        ],
      },
      {
        heading: "Roof Washing: Every 2-3 Years",
        paragraphs: [
          "Roofs generally need less frequent cleaning than siding, but freeze-thaw cycling makes existing algae colonies more entrenched over time, since moisture gets trapped under growth and around shingle tabs during winter. Catching streaking early — before it's had multiple winters to set in — makes each cleaning more effective and helps protect shingle life. Most homes are well-served by a roof soft wash every two to three years, though heavily shaded roofs may need it closer to the two-year mark.",
        ],
      },
      {
        heading: "Concrete & Driveways: Annually, Especially After Winter",
        paragraphs: [
          "Concrete takes the most direct hit from Upstate NY winters. Road salt, sand, and de-icing chemicals get tracked onto driveways and walkways all winter, and freeze-thaw cycling drives that residue into the concrete's pores, where it can contribute to surface scaling and spalling over time if never cleaned out. An annual spring cleaning — after the last hard freeze but before summer — removes salt residue, embedded grime, and the winter's buildup of algae in shaded areas, and it's also the ideal time to reseal if your concrete or pavers are due.",
        ],
        bullets: [
          "Clean driveways and walkways every spring to remove salt and de-icing residue",
          "Reseal concrete and pavers every 2-3 years to protect against freeze-thaw damage",
          "Address dark, damp staining promptly — it usually signals trapped moisture and algae",
        ],
      },
      {
        heading: "Decks, Fences, and Gutters: Seasonal Checks",
        paragraphs: [
          "Wood decks and fences benefit from a cleaning (and resealing, if applicable) once a year, typically in spring, since winter moisture and freeze-thaw swelling can loosen old sealant and trap grime in the wood grain. Gutters should be checked and cleaned at least twice a year — after leaf drop in fall and again in spring — since clogged gutters holding water through freeze-thaw cycles are a major contributor to ice dams and fascia damage on Capital Region homes.",
        ],
      },
    ],
    faqs: [
      {
        q: "Is it safe to pressure wash in winter in the Capital Region?",
        a: "We recommend waiting until temperatures are reliably above freezing. Washing surfaces that then freeze can trap moisture and worsen damage, so most jobs run spring through fall.",
      },
      {
        q: "Does freeze-thaw damage make cleaning more urgent?",
        a: "Yes — the longer grime, salt, and algae sit through repeated freeze-thaw cycles, the deeper they work into pores and seams, making eventual cleaning harder and increasing the risk of surface damage like concrete spalling.",
      },
      {
        q: "Should I clean my concrete before or after sealing?",
        a: "Always clean first. Sealing over embedded dirt, salt, or algae traps it under the sealant and shortens how long the seal lasts.",
      },
      {
        q: "What's the best single time of year to clean my whole property?",
        a: "Spring is popular because it addresses winter's salt, sand, and trapped moisture all at once, but many homeowners split services — house and roof in fall, concrete in spring.",
      },
      {
        q: "Can regular cleaning actually extend the life of my siding or concrete?",
        a: "Yes. Removing algae, salt, and trapped grime before freeze-thaw cycles work it deeper into the surface reduces long-term wear and helps sealants and paint last longer.",
      },
    ],
    relatedLinks: [
      { label: "Concrete & Driveway Cleaning", to: "/concrete-cleaning-sealing" },
      { label: "House Washing Service", to: "/house-washing" },
      { label: "Roof Washing Service", to: "/roof-washing" },
    ],
  },
  {
    slug: "house-washing-cost-albany",
    metaTitle: "House Washing Cost in Albany NY (2026)",
    title: "How Much Does House Washing Cost in Albany, NY?",
    description:
      "What house washing actually costs in Albany and the Capital Region, what drives the price up or down, and how to tell a fair quote from a bad one.",
    published: "2026-08-18",
    heroImage: houseWashingHero.url,
    intro:
      "Most single-family house washing jobs in Albany and the wider Capital Region land between $299 and $549. That's a real range, not a dodge — the same crew, the same equipment, and the same detergent can produce a $310 job on one street and a $520 job two blocks over. Here's exactly what moves the number, so you can read any quote you're given and know whether it's fair.",
    sections: [
      {
        heading: "The Four Things That Set the Price",
        paragraphs: [
          "Square footage of siding comes first. Pricing follows wall area, not the square footage listed on your tax bill — a sprawling ranch can have less siding than a compact two-story colonial with the same floor plan.",
          "Height and access come second. A single-story home is washed from the ground. Three stories, dormers, steep grade, or tight side yards mean more setup, more equipment, and more time on each elevation.",
          "Material is third. Vinyl and aluminum are straightforward. Cedar, stucco, and heavily detailed painted trim take a gentler mix and longer dwell time, which costs more labor.",
          "Condition is fourth and it's the one homeowners underestimate. A house on an annual wash cycle needs one application. A house that hasn't been touched in six years, with deep algae on the north wall and black mildew tracking down from the gutters, needs more solution, more dwell, and hand detailing.",
        ],
        bullets: [
          "One-story ranch, vinyl, light growth: low end of the range",
          "Two-story colonial, vinyl, annual maintenance: middle of the range",
          "Large or three-story home, cedar or stucco, heavy algae: top of the range or above",
        ],
      },
      {
        heading: "What Should Be Included in the Price",
        paragraphs: [
          "A complete house wash quote in the Capital Region should cover all elevations of siding, trim, soffits, the gutter exteriors, window exteriors rinsed, and downspouts. It should also include protecting your landscaping — soaking beds before, covering sensitive plantings during, and rinsing thoroughly afterward.",
          "Be careful with quotes that sound cheap and then bill separately for the back of the house, for the second story, or for 'heavy soiling.' Our quotes are one firm written number for the scope you approved, and the price does not change on the day of service.",
        ],
      },
      {
        heading: "Why a Cheap Pressure Wash Costs More Long Term",
        paragraphs: [
          "The lowest bids usually come from someone with a consumer pressure washer and no detergent budget. High pressure on siding can crack aging vinyl, force water behind the panels, cut caulk lines at seams and windows, and strip paint off wood trim. Repairs cost multiples of what the wash saved.",
          "It also doesn't last. Blasting removes what's visible; the algae and mildew roots stay in the surface texture and the green returns within a season. A proper soft wash kills the growth at the root, which is why the results hold for one to three years.",
        ],
      },
      {
        heading: "How to Pay Less Without Cutting Corners",
        paragraphs: [
          "Bundle. Most of what any exterior job costs is setup, water, and travel, so adding the driveway, gutters, or roof to the same visit is far cheaper than three separate trips — bundles typically save around 10%.",
          "Stay on a yearly schedule. Annual washes are lighter, faster, and cheaper than a rescue wash after five years of buildup.",
          "Ask about discounts. Active and retired military, first responders, and seniors receive a discount on every job — just mention it when you request the quote.",
          "Get the quote remotely. We measure and price most Capital Region homes from satellite imagery, which means no waiting around for an appointment before you know the number.",
        ],
      },
    ],
    faqs: [
      {
        q: "What is the average cost to wash a house in Albany NY?",
        a: "Most single-family homes in Albany and the surrounding Capital Region fall between $299 and $549, depending on siding square footage, number of stories, material, and how heavy the algae and mildew growth is.",
      },
      {
        q: "Is house washing cheaper than painting?",
        a: "Dramatically. A wash is a few hundred dollars and often makes repainting unnecessary for years, because much of what looks like faded paint is actually algae, mildew, and surface grime.",
      },
      {
        q: "Do you charge extra for two-story homes?",
        a: "Height is factored into the single quoted price rather than added later as a surprise line item. You get one firm number before we schedule.",
      },
      {
        q: "Can you quote without visiting my home?",
        a: "Yes. Most Capital Region house washes are measured and priced remotely using satellite imagery. Free on-site quotes are available whenever you'd prefer we walk the property.",
      },
      {
        q: "How often should I budget for it?",
        a: "Once a year for most Capital Region homes, and every 18 months at minimum for shaded elevations. Annual washes cost less per visit than infrequent heavy ones.",
      },
    ],
    relatedLinks: [
      { label: "House Washing Service", to: "/house-washing" },
      { label: "Vinyl Siding Cleaning", to: "/vinyl-siding-cleaning" },
      { label: "Exterior House Cleaning", to: "/exterior-house-cleaning" },
      { label: "House Washing in Albany, NY", to: "/house-washing-albany-ny" },
    ],
  },
  {
    slug: "roof-cleaning-cost-albany",
    metaTitle: "Roof Cleaning Cost in Albany NY (2026)",
    title: "How Much Does Roof Cleaning Cost in Albany, NY?",
    description:
      "What roof cleaning actually costs in Albany and the Capital Region, the four things that set the price, and how to tell a real quote from a lowball.",
    published: "2026-08-18",
    heroImage: roofWashingHero.url,
    intro:
      "Most single-family roof cleanings in Albany and the wider Capital Region land between $499 and $1,199. That's a wide band on purpose — a one-story ranch with light streaking and a steep three-story with moss mats in the valleys are genuinely different jobs. Here's exactly what moves the number, so you can read any quote you're handed and know whether it's honest.",
    sections: [
      {
        heading: "The Four Things That Set the Price",
        paragraphs: [
          "Roof surface square footage comes first, and it is not the same as the square footage on your tax bill. A steep pitch adds a lot of surface over the same floor plan, which is why two houses that look similar from the street can be priced hundreds of dollars apart.",
          "Pitch is second, and it drives both surface area and difficulty. A walkable low-slope roof is straightforward; a steep pitch means everything is worked from ladders and the ground with longer reach and more setup.",
          "Access is third. Stories, deck overhangs, tight side yards, fenced gates, and landscaping the crew has to protect all change how long the job takes before a drop of solution is applied.",
          "Growth severity is fourth. Light black streaking on one slope needs one pass. Heavy moss mats and rooted lichen need substantially more solution and much longer dwell time, and the roof will keep releasing material for weeks afterward.",
        ],
        bullets: [
          "One-story ranch, light streaking on the north slope: low end of the range",
          "Two-story colonial, moderate streaking across several slopes: middle of the range",
          "Steep or three-story roof with established moss and lichen: top of the range",
        ],
      },
      {
        heading: "Beware the Flat Phone Quote",
        paragraphs: [
          "Any company that gives you one flat number over the phone without looking at the roof is guessing, and that guess almost always becomes a change order once the truck is in your driveway. Roof pricing depends on things nobody can know from a phone call.",
          "We measure roof surface, pitch, and access remotely from satellite imagery and send one firm written number, usually within a business day. That price is the price. Free on-site quotes are available any time you'd rather we walk the property first.",
        ],
      },
      {
        heading: "Why the Cheapest Bid Is Usually a Pressure Washer",
        paragraphs: [
          "The lowest bids on roof work almost always come from someone planning to pressure wash it. High pressure strips the protective granules off asphalt shingles — the exact layer shielding them from UV and weather — and typically voids the shingle manufacturer's warranty the moment the wand touches the roof.",
          "It doesn't last either. Blasting removes the visible stain and leaves the algae roots in place, so the streaks come back within a season or two. A proper soft wash kills Gloeocapsa magma, moss, and lichen at the root, which is why the result holds three to five years.",
          "Put plainly: a $300 pressure wash that takes five years off a $14,000 roof is not the cheap option.",
        ],
      },
      {
        heading: "How to Pay Less Without Cutting Corners",
        paragraphs: [
          "Bundle it. Most of the cost on any exterior job is setup, water, and travel, so having the roof and the house siding done on the same visit removes an entire mobilization and typically saves around 10%.",
          "Don't wait for the moss. Light streaking is a one-pass job. Established moss and lichen need far more product and dwell time, so the same roof costs more the longer it's left.",
          "Ask about discounts. Active and retired military, first responders, and seniors get a discount on every job — just mention it when you request the quote.",
          "Get the quote remotely. Most Capital Region roofs are measured and priced from satellite imagery, so you get a firm number without waiting on an appointment.",
        ],
      },
    ],
    faqs: [
      {
        q: "What is the average cost of roof cleaning in Albany NY?",
        a: "Most single-family roofs in Albany and the surrounding Capital Region fall between $499 and $1,199, depending on roof surface square footage, pitch, access, and how heavy the algae or moss growth is.",
      },
      {
        q: "Is roof cleaning cheaper than roof replacement?",
        a: "Dramatically. A clean is several hundred dollars and stops the granule loss that causes shingles to fail early; a replacement in this market runs well into five figures.",
      },
      {
        q: "Does heavy moss cost more than black streaks?",
        a: "Yes. Moss mats and rooted lichen need more solution and much longer dwell time than surface algae, so they sit at the higher end of the range.",
      },
      {
        q: "Can you quote a roof without coming out?",
        a: "Usually. We measure surface area, pitch, and access from satellite imagery and send a firm written price, typically within a business day. Free on-site quotes are always available.",
      },
      {
        q: "How often should I budget for roof cleaning?",
        a: "Every three to five years for most Capital Region roofs, and toward the shorter end if your roof sits under heavy tree cover with shaded slopes.",
      },
    ],
    relatedLinks: [
      { label: "Roof Cleaning Service", to: "/roof-washing" },
      { label: "Roof Moss Removal", to: "/roof-moss-removal" },
      { label: "Roof Washing Service", to: "/roof-washing" },
      { label: "Roof Cleaning in Albany, NY", to: "/roof-washing-albany-ny" },
    ],
  },
  {
    slug: "deck-cleaning-cost-albany",
    metaTitle: "Deck Cleaning Cost in Albany NY (2026)",
    title: "How Much Does Deck Cleaning Cost in Albany NY? (2026 Pricing Guide)",
    description:
      "What deck cleaning costs in Albany and the Capital Region, what changes the price, and how wood and composite decks differ.",
    published: "2026-08-19",
    heroImage: houseWashingHero.url,
    intro:
      "Deck cleaning is one of the least expensive things you can do to a house and one of the easiest to overpay for, because almost nobody quotes it the same way. Here is what deck cleaning actually costs in Albany and the surrounding Capital Region, what moves the number up or down, and how to tell a real deck cleaning from someone about to fuzz your boards with a turbo tip.",
    sections: [
      {
        heading: "The Short Answer",
        paragraphs: [
          "Most deck cleanings in the Capital Region run $200 to $450. A small 200-square-foot pressure-treated deck with a single stair run sits at the bottom of that range. A large multi-level deck with heavy railing footage, built-in benches, and a lattice skirt sits at the top.",
          "Adding a brightener is usually modest. Adding stain or sealer is a separate job with its own material cost and is typically quoted per square foot after the cleaning is scheduled.",
        ],
        bullets: [
          "Small deck, 150–250 sq ft, minimal railing: $200–$275",
          "Average deck, 250–400 sq ft with stairs and railings: $275–$375",
          "Large or multi-level deck with heavy railing and skirting: $375–$450+",
        ],
      },
      {
        heading: "What Actually Changes the Price",
        paragraphs: [
          "Square footage is the obvious one, but railing footage matters more than most homeowners expect. Spindles are slow, detailed work — a deck with 60 feet of railing can take longer than the deck floor itself.",
          "Material matters second. Pressure-treated pine, cedar, and exotic hardwoods each want a different cleaner and a different dwell time. Composite and Trex need a manufacturer-approved mix that removes the surface mold film without hazing the cap layer.",
          "Condition is third. A deck cleaned last year needs one pass. A deck that has gone five years, grayed out, and grown a mildew film on the shaded half needs a stronger mix and more time.",
          "Access is fourth. A second-story deck over a slope, or one with no water spigot nearby, adds setup time that shows up in the price.",
        ],
      },
      {
        heading: "Wood vs. Composite Deck Cleaning",
        paragraphs: [
          "Wood decks gray because UV oxidizes the surface fibers. That gray lifts with a proper cleaner, and a brightener afterward evens the tone and balances pH so stain bonds correctly. If you plan to stain, cleaning is not optional — stain over a mildewed deck traps the growth underneath.",
          "Composite decks do not gray the same way. What looks like staining on Trex or similar boards is almost always mold and mildew growing in the surface texture, plus tannin from leaves left over the winter. Cleaning is the only fix, because you cannot stain a composite deck to hide it. The good news is composite responds fast to the right mix.",
        ],
      },
      {
        heading: "Why the Cheapest Bid Is Usually a Pressure Washer",
        paragraphs: [
          "The lowest deck bids come from someone planning to blast the boards with a narrow tip. It looks clean for a week, then the raised grain starts catching dirt, splinters show up under bare feet, and the wand stripes never fully disappear.",
          "Proper deck cleaning is chemistry plus dwell time, with pressure low enough that the boards stay smooth. It costs a little more up front and it does not shorten the life of a deck that cost thousands to build.",
        ],
      },
      {
        heading: "How to Pay Less Without Cutting Corners",
        paragraphs: [
          "Bundle it. Most of the cost of any exterior job is setup, water, and travel. Having the deck cleaned during a house wash removes an entire mobilization and usually saves around 10%.",
          "Do it annually. A deck cleaned every spring is a one-pass job. A deck left five years takes far more product and time for the same result.",
          "Ask about discounts. Active and retired military, first responders, and seniors get a discount on every job.",
          "Clear the deck yourself. Moving furniture, grills, and planters before the crew arrives keeps labor time down.",
        ],
      },
    ],
    faqs: [
      { q: "How much does deck cleaning cost in Albany NY?", a: "Most decks in Albany and the Capital Region cost $200 to $450 to clean, depending on square footage, railing footage, stairs, material, and how much graying and mildew has built up." },
      { q: "Is deck cleaning worth it before staining?", a: "It is required. Stain applied over mildew and surface film traps the growth and peels early, so cleaning and brightening first is what makes the stain last." },
      { q: "How much does it cost to clean a composite deck?", a: "Composite deck cleaning is priced similarly to wood, generally $200 to $450 depending on size and railings. Composite usually needs less dwell time but the same careful low-pressure rinse." },
      { q: "How long does deck cleaning take?", a: "Most decks are done in two to four hours. Large multi-level decks with heavy railing can take most of a day." },
      { q: "How soon can I stain after cleaning?", a: "Give the boards 24 to 72 hours to dry completely, depending on humidity and sun exposure." },
    ],
    relatedLinks: [
      { label: "Deck Cleaning Service", to: "/deck-patio-cleaning" },
      { label: "Deck Cleaning in Albany, NY", to: "/deck-cleaning-albany-ny" },
      { label: "House Washing Cost in Albany", to: "/guides/house-washing-cost-albany" },
      { label: "Soft Washing", to: "/soft-washing" },
    ],
  },
];


export const GUIDES: Guide[] = [
  ...GUIDES_CORE,
  ...GUIDES_HARDSCAPE,
  ...GUIDES_SEASONAL,
  ...GUIDES_COST,
];

export function getGuide(slug: string): Guide | undefined {
  return GUIDES.find((g) => g.slug === slug);
}
